import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Trophy, Mail, FileText, ClipboardCheck, Home, PoundSterling } from 'lucide-react';

interface WonStageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerName: string;
  // Stored deal value is in pence; the input and confirmation use pounds.
  currentDealValue?: number;
  onConfirm: (preferences: {
    sendSolarJourney: boolean;
    generateLOA: boolean;
    triggerOnboarding: boolean;
    createInstallation: boolean;
    dealValue?: number;
  }) => void;
}

export function WonStageModal({
  open,
  onOpenChange,
  customerName,
  currentDealValue,
  onConfirm,
}: WonStageModalProps) {
  const [sendSolarJourney, setSendSolarJourney] = useState(true);
  const [generateLOA, setGenerateLOA] = useState(true);
  const [triggerOnboarding, setTriggerOnboarding] = useState(true);
  const [createInstallation, setCreateInstallation] = useState(true);
  const [dealValue, setDealValue] = useState((currentDealValue || 0) / 100);
  const [dealValueError, setDealValueError] = useState(false);

  const handleConfirm = () => {
    // Validate deal value is provided and greater than 0
    if (!dealValue || dealValue <= 0) {
      setDealValueError(true);
      return;
    }

    onConfirm({
      sendSolarJourney,
      generateLOA,
      triggerOnboarding,
      createInstallation,
      dealValue: dealValue,
    });
    onOpenChange(false);
  };

  const handleCancel = () => {
    // Reset to defaults when canceling
    setSendSolarJourney(true);
    setGenerateLOA(true);
    setTriggerOnboarding(true);
    setCreateInstallation(true);
    setDealValue((currentDealValue || 0) / 100);
    setDealValueError(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Congratulations - Deal Won!
          </DialogTitle>
          <DialogDescription>
            Moving <strong>{customerName}</strong> to Won stage. Select the automated actions you'd like to perform:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Deal Value Input */}
          <div className="space-y-2">
            <Label htmlFor="dealValue" className="flex items-center gap-2">
              <PoundSterling className="h-4 w-4" />
              Deal Value <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
              <Input
                id="dealValue"
                type="number"
                placeholder="0.00"
                value={dealValue}
                onChange={(e) => {
                  setDealValue(parseFloat(e.target.value) || 0);
                  setDealValueError(false);
                }}
                className={`w-full pl-8 ${dealValueError ? 'border-red-500 focus:ring-red-500' : ''}`}
              />
            </div>
            {dealValueError && (
              <p className="text-xs text-red-500">
                Deal value is required and must be greater than £0
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Enter the final deal value for this customer (required)
            </p>
          </div>

          {/* Automation Options */}
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="sendSolarJourney"
                checked={sendSolarJourney}
                onCheckedChange={(checked) => setSendSolarJourney(checked as boolean)}
              />
              <div className="space-y-1">
                <Label
                  htmlFor="sendSolarJourney"
                  className="flex items-center gap-2 font-medium cursor-pointer"
                >
                  <Mail className="h-4 w-4 text-blue-500" />
                  Send Solar Journey Email
                </Label>
                <p className="text-xs text-muted-foreground">
                  Send congratulations email with Solar Installation Journey PDF
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                id="generateLOA"
                checked={generateLOA}
                onCheckedChange={(checked) => setGenerateLOA(checked as boolean)}
              />
              <div className="space-y-1">
                <Label
                  htmlFor="generateLOA"
                  className="flex items-center gap-2 font-medium cursor-pointer"
                >
                  <FileText className="h-4 w-4 text-green-500" />
                  Generate Letter of Authority
                </Label>
                <p className="text-xs text-muted-foreground">
                  Create and send LOA for customer signature
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                id="triggerOnboarding"
                checked={triggerOnboarding}
                onCheckedChange={(checked) => setTriggerOnboarding(checked as boolean)}
              />
              <div className="space-y-1">
                <Label
                  htmlFor="triggerOnboarding"
                  className="flex items-center gap-2 font-medium cursor-pointer"
                >
                  <ClipboardCheck className="h-4 w-4 text-purple-500" />
                  Trigger Onboarding Process
                </Label>
                <p className="text-xs text-muted-foreground">
                  Start the customer onboarding workflow
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                id="createInstallation"
                checked={createInstallation}
                onCheckedChange={(checked) => setCreateInstallation(checked as boolean)}
              />
              <div className="space-y-1">
                <Label
                  htmlFor="createInstallation"
                  className="flex items-center gap-2 font-medium cursor-pointer"
                >
                  <Home className="h-4 w-4 text-orange-500" />
                  Create Installation Record
                </Label>
                <p className="text-xs text-muted-foreground">
                  Automatically create an installation entry for this customer
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            className="bg-green-600 hover:bg-green-700"
            disabled={!dealValue || dealValue <= 0}
          >
            Confirm & Move to Won
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
